import { Button, Stack, Step, StepLabel, Stepper } from '@mui/material'
import React, { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'

import PageSection from 'components/common/PageSection'
import { StatusCheckConnectionForm } from 'components/status-checks/common/StatusCheckConnectionForm'
import { getIsDynatraceIntelligentHealthCheckRequest } from 'components/status-checks/create/create-status-check-utils'
import { ObservabilityToolAuthentication } from 'components/status-checks/form/authentication/ObservabilityToolAuthentication'
import { SaveStatusCheckFormButton } from 'components/status-checks/form/status-check/SaveStatusCheckFormButton'
import { useStatusCheckFormValidationSchemaBuilder } from 'components/status-checks/hooks/use-status-check-form-validation-schema-builder'
import { useStatusCheckRequestBuilder } from 'components/status-checks/hooks/use-status-check-request-builder'
import {
  DEFAULT_QUERY_STATUS_CHECKS_STALE_TIME,
  getDefaultStatusCheckFormValues,
  type StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { StatusCheckDetailsProvider } from 'context/status-checks/StatusCheckDetailsContext'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import toast from 'lib/toast'
import { useUpdateService } from 'queries/services/use-update-service'
import { useCreateDynatraceStatusCheck } from 'queries/status-checks/use-create-dynatrace-status-check'
import { useCreateStatusCheck } from 'queries/status-checks/use-create-status-check'
import { useGetStatusChecksAsServiceStatusChecks } from 'queries/status-checks/use-get-status-checks-as-service-status-checks'
import { handleDataFetchOnChange } from 'utils/handle-data-fetch-on-change'
import { messageFromResponseError } from 'utils/strings'
import { ms } from 'utils/time/millisecond'

import type { UserDefinedService } from 'types/api/services/service'

const STEPS = ['Authenticate', 'Connect Monitor or Alert']

export const CreateStatusCheckForm: React.FC<{
  service?: UserDefinedService
}> = ({ service }) => {
  const [activeStep, setActiveStep] = useState(0)
  const navigate = useNavigate()
  const location = useLocation()
  const statusChecksUrls = getStatusChecksUrls()
  const { referrer = statusChecksUrls.base() } = location.state ?? {}

  const { mutateAsync: createStatusCheck } = useCreateStatusCheck()
  const { mutateAsync: createDynatraceStatusCheck } =
    useCreateDynatraceStatusCheck()
  const { mutateAsync: updateService } = useUpdateService(service?.serviceId)
  const {
    data: existingStatusChecks,
    error: fetchExistingStatusChecksError,
    isError: hasErrorExistingStatusChecks,
    refetch: reFetchStatusChecks
  } = useGetStatusChecksAsServiceStatusChecks({
    retryOnMount: false,
    staleTime: DEFAULT_QUERY_STATUS_CHECKS_STALE_TIME
  })

  const {
    getAvailableExternalIntegrations,
    hasErrorExternalIntegrations,
    isFetchingExternalIntegrations,
    externalIntegrationsError
  } = useStatusCheckExternalIntegrationsContext()
  const externalIntegrations = getAvailableExternalIntegrations(false)

  const buildStatusCheckFormValidationSchema =
    useStatusCheckFormValidationSchemaBuilder(
      externalIntegrations,
      existingStatusChecks ?? []
    )

  const form = useForm<StatusCheckFormValues, number>({
    context: activeStep,
    mode: 'onChange',
    defaultValues: getDefaultStatusCheckFormValues(),
    resolver: buildStatusCheckFormValidationSchema
  })

  const fetchExistingStatusChecksErrorMessage =
    hasErrorExistingStatusChecks &&
    messageFromResponseError(
      fetchExistingStatusChecksError,
      'An error occurred while fetching existing Health Checks'
    )

  const fetchExternalIntegrationsErrorMessage =
    hasErrorExternalIntegrations &&
    messageFromResponseError(
      externalIntegrationsError,
      'An error occurred while fetching external integrations'
    )

  const { handleSubmit } = form

  const handleNext = async () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
    form.reset()
  }

  const buildSaveStatusCheckRequest = useStatusCheckRequestBuilder()
  const handleSaveStatusCheck = async (values: StatusCheckFormValues) => {
    try {
      const requestPayload = await buildSaveStatusCheckRequest(values)
      const isDynatraceIntelligentHealthCheckRequest =
        getIsDynatraceIntelligentHealthCheckRequest(requestPayload)
      const newStatusCheckId = isDynatraceIntelligentHealthCheckRequest
        ? (await createDynatraceStatusCheck(requestPayload)).identifier
        : await createStatusCheck(requestPayload)

      if (service) {
        await updateService({
          statusCheckIds: [...(service.statusCheckIds ?? []), newStatusCheckId]
        })
      }

      toast({
        type: 'success',
        text: 'Health Check saved successfully.',
        time: ms.seconds(2)
      })

      // Refresh status checks list
      await handleDataFetchOnChange(reFetchStatusChecks)
      navigate(referrer)
    } catch (err) {
      const error = messageFromResponseError(
        err,
        'There was an issue saving this Health Check. Please try again later.'
      )
      toast({ type: 'error', text: error })
    }
  }

  return (
    <PageSection
      errorMessage={
        fetchExistingStatusChecksErrorMessage ??
        fetchExternalIntegrationsErrorMessage
      }
      hasError={hasErrorExistingStatusChecks || hasErrorExternalIntegrations}
      isLoading={isFetchingExternalIntegrations}
    >
      <Stack gap={3}>
        <Stepper activeStep={activeStep} sx={{ maxWidth: 'sm' }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <FormProvider {...form}>
          <StatusCheckDetailsProvider serviceName={service?.name}>
            <form onSubmit={handleSubmit(handleSaveStatusCheck)}>
              <Stack spacing={2}>
                {activeStep === 0 ? (
                  <ObservabilityToolAuthentication
                    externalIntegrations={externalIntegrations}
                    onNext={handleNext}
                  />
                ) : (
                  <Stack spacing={4}>
                    <StatusCheckConnectionForm />
                    <Stack direction="row" sx={{ columnGap: 2 }}>
                      <Button variant="contained" onClick={handleBack}>
                        Back
                      </Button>
                      <SaveStatusCheckFormButton
                        data-testid="create-status-check-save-button"
                        label="Create Health Check"
                      />
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </form>
          </StatusCheckDetailsProvider>
        </FormProvider>
      </Stack>
    </PageSection>
  )
}
